/** Native file picker (File System Access API) with <input type="file"> fallback. */

export type PickedCadFile = {
  file: File;
  handle?: FileSystemFileHandle;
};

const PICKER_TYPES = [
  {
    description: 'STEP / IGES',
    accept: {
      'model/step': ['.step', '.stp'],
      'model/iges': ['.iges', '.igs'],
      'application/octet-stream': ['.step', '.stp', '.iges', '.igs']
    }
  },
  {
    description: 'Mesh (glTF / OBJ / STL / PLY / 3MF)',
    accept: {
      'model/gltf-binary': ['.glb'],
      'model/gltf+json': ['.gltf'],
      'model/obj': ['.obj'],
      'model/stl': ['.stl'],
      'model/3mf': ['.3mf'],
      'application/ply': ['.ply'],
      'application/octet-stream': ['.glb', '.gltf', '.obj', '.stl', '.ply', '.3mf']
    }
  }
] as const;

type OpenFilePickerWindow = Window & {
  showOpenFilePicker?: (options?: {
    multiple?: boolean;
    excludeAcceptAllOption?: boolean;
    types?: ReadonlyArray<{
      description?: string;
      accept: Record<string, readonly string[]>;
    }>;
  }) => Promise<FileSystemFileHandle[]>;
};

export function canUseFilePicker(): boolean {
  return typeof (window as OpenFilePickerWindow).showOpenFilePicker === 'function';
}

/** Opens the OS file picker. Returns null when the user cancels. */
export async function pickCadFile(): Promise<PickedCadFile | null> {
  const showOpenFilePicker = (window as OpenFilePickerWindow).showOpenFilePicker;
  if (!showOpenFilePicker) return null;

  try {
    const [handle] = await showOpenFilePicker({
      multiple: false,
      excludeAcceptAllOption: false,
      types: [...PICKER_TYPES]
    });
    if (!handle) return null;
    const file = await handle.getFile();
    return { file, handle };
  } catch (err) {
    // AbortError = user cancelled the dialog.
    if (err instanceof DOMException && err.name === 'AbortError') return null;
    throw err;
  }
}
